import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { AdminTable } from './AdminTable';
import type { NotificationSettingsWithLocation } from '../services/notificationSettingsService';

interface NotificationsTableProps {
  notifications: NotificationSettingsWithLocation[];
  onEdit: (notification: NotificationSettingsWithLocation) => void;
  isLoading?: boolean;
}

const NOTIFICATION_TYPE_LABELS: Record<string, string> = {
  FRAMES_PENDING: 'Frames Pending',
  FRAMES_ORDERED_NOT_RECEIVED: 'Ordered Not Received',
  LOCATION_INACTIVE: 'Location Inactive',
};

export function NotificationsTable({
  notifications,
  onEdit,
  isLoading = false,
}: NotificationsTableProps) {
  const columns = [
    {
      key: 'notification_type',
      header: 'Type',
      sortable: true,
      render: (notification: NotificationSettingsWithLocation) => (
        <div className="font-medium">
          {NOTIFICATION_TYPE_LABELS[notification.notification_type] ||
            notification.notification_type}
        </div>
      ),
    },
    {
      key: 'location',
      header: 'Location',
      render: (notification: NotificationSettingsWithLocation) => (
        <div className="text-sm">
          {notification.location ? (
            <span>{notification.location.name}</span>
          ) : (
            <span className="text-gray-500 italic">All Locations</span>
          )}
        </div>
      ),
    },
    {
      key: 'threshold_days',
      header: 'Threshold',
      sortable: true,
      render: (notification: NotificationSettingsWithLocation) => (
        <div className="text-sm">{notification.threshold_days} days</div>
      ),
    },
    {
      key: 'frequency',
      header: 'Frequency',
      sortable: true,
      render: (notification: NotificationSettingsWithLocation) => (
        <Badge variant="neutral">{notification.frequency}</Badge>
      ),
    },
    {
      key: 'recipient_emails',
      header: 'Recipients',
      render: (notification: NotificationSettingsWithLocation) => (
        <div className="text-sm text-gray-600">
          {notification.recipient_emails.length} recipient
          {notification.recipient_emails.length !== 1 ? 's' : ''}
          {notification.cc_regional && ' + Regional'}
        </div>
      ),
    },
    {
      key: 'enabled',
      header: 'Status',
      sortable: true,
      render: (notification: NotificationSettingsWithLocation) => (
        <Badge variant={notification.enabled ? 'success' : 'neutral'}>
          {notification.enabled ? 'Enabled' : 'Disabled'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (notification: NotificationSettingsWithLocation) => (
        <Button size="sm" variant="ghost" onClick={() => onEdit(notification)}>
          Edit
        </Button>
      ),
    },
  ];

  return (
    <AdminTable
      data={notifications}
      columns={columns}
      isLoading={isLoading}
      emptyMessage="No notification settings found"
    />
  );
}
